import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type AdminTrainerClassSummary = {
  id: number;
  name: string;
};

export type AdminTrainerOverview = {
  id: number;
  username: string;
  klasses: AdminTrainerClassSummary[];
};

export type AdminTrainerClassTrainee = {
  id: number;
  username: string;
  can_see_devplan: boolean;
  can_see_recommendations: boolean;
};

export type AdminTrainerClassDetail = {
  id: number;
  name: string;
  has_sessions: boolean;
  trainees: AdminTrainerClassTrainee[];
};

type ApiMethod = 'GET' | 'POST' | 'DELETE';

const apiErrorMessage = (result: unknown, fallback: string): string => {
  if (typeof result === 'string' && result) return result;
  if (result && typeof result === 'object') {
    const messages = Object.values(result as Record<string, unknown>).flatMap(
      value => (Array.isArray(value) ? value : [value]),
    );
    if (messages.length) return messages.map(String).join(' ');
  }
  return fallback;
};

const request = async <Payload, Result>(
  path: string,
  method: ApiMethod,
  data: Payload,
  fallback: string,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  if (response.status.toString()[0] !== '2') {
    throw new Error(apiErrorMessage(response.result, fallback));
  }
  return response.result as Result;
};

export const fetchAdminTrainerOverview = () =>
  request<Record<string, never>, AdminTrainerOverview[]>(
    '/trainers/overview',
    'GET',
    {},
    'The trainer list could not be loaded.',
  );

export const fetchAdminTrainerClassDetail = (klassId: number) =>
  request<{ klass_id: number }, AdminTrainerClassDetail>(
    '/trainers/class_detail',
    'GET',
    { klass_id: klassId },
    'The class details could not be loaded.',
  );

export const updateAdminTrainerPassword = (
  trainerId: number,
  password: string,
) =>
  request<{ trainer_id: number; password: string }, null>(
    '/trainers/:trainer_id/set_password',
    'POST',
    { trainer_id: trainerId, password },
    'The trainer password could not be updated.',
  );

export const updateAdminTraineeDevplanAccess = (
  klassId: number,
  traineeId: number,
  canSeeDevplan: boolean,
) =>
  request<
    {
      klass_id: number;
      trainee_id: number;
      can_see_devplan: boolean;
    },
    {
      klass_id: number;
      trainee_id: number;
      can_see_devplan: boolean;
    }
  >(
    '/users/set_devplan_access',
    'POST',
    {
      klass_id: klassId,
      trainee_id: traineeId,
      can_see_devplan: canSeeDevplan,
    },
    'Development Plan access could not be updated.',
  );

export const updateAdminTraineeRecommendationAccess = (
  klassId: number,
  traineeId: number,
  canSeeRecommendations: boolean,
) =>
  request<
    {
      klass_id: number;
      trainee_id: number;
      can_see_recommendations: boolean;
    },
    {
      klass_id: number;
      trainee_id: number;
      can_see_recommendations: boolean;
    }
  >(
    '/users/set_recommendation_access',
    'POST',
    {
      klass_id: klassId,
      trainee_id: traineeId,
      can_see_recommendations: canSeeRecommendations,
    },
    'Recommendation access could not be updated.',
  );

export const deleteAdminTrainee = (traineeId: number) =>
  request<{ trainee_id: number }, { trainee_id: number; klass_id: number }>(
    '/users/delete_trainee',
    'DELETE',
    { trainee_id: traineeId },
    'The trainee could not be deleted.',
  );

export const addAdminTrainee = (
  klassId: number,
  username: string,
  password: string,
) =>
  request<
    { klass_id: number; username: string; password: string },
    { trainee: AdminTrainerClassTrainee; klass_id: number }
  >(
    '/klasses/add_new_user_to_klass',
    'POST',
    { klass_id: klassId, username, password },
    'The trainee could not be created.',
  );

export type KlassClientObserver = {
  id: number;
  username: string;
};

export const fetchKlassClients = (klassId: number) =>
  request<Record<string, never>, KlassClientObserver[]>(
    `/klasses/${klassId}/clients`,
    'GET',
    {},
    'The client observers could not be loaded.',
  );

export const addClientToKlass = (
  klassId: number,
  username: string,
  password?: string,
) =>
  request<
    { klass_id: number; username: string; password?: string },
    { client: KlassClientObserver; klass_id: number }
  >(
    '/klasses/add_client_to_klass',
    'POST',
    { klass_id: klassId, username, ...(password ? { password } : {}) },
    'The client observer could not be added.',
  );

export const removeClientFromKlass = (klassId: number, clientId: number) =>
  request<
    { klass_id: number; client_id: number },
    { client_id: number; klass_id: number }
  >(
    '/klasses/remove_client_from_klass',
    'POST',
    { klass_id: klassId, client_id: clientId },
    'The client observer could not be removed.',
  );

export const deleteAdminTrainerClass = (klassId: number) =>
  request<{ klass_id: number }, { klass_id: number; trainer_id: number }>(
    '/klasses/delete_klass',
    'DELETE',
    { klass_id: klassId },
    'The class could not be deleted.',
  );
